import type { AudioFrame, EmotionLabel } from '../types';

export interface EmotionResult {
  label: string;
  confidence: number;
}

export type { EmotionLabel };

const LABEL_MAP: Record<string, EmotionLabel> = {
  angry: 'angry',
  calm: 'neutral',
  disgust: 'disgust',
  fearful: 'fear',
  happy: 'happy',
  sad: 'sad',
  surprised: 'surprise',
};

const TARGET_SAMPLE_RATE = 16000;
const BUFFER_TARGET_SECONDS = 1.5;
const MAX_BUFFER_SAMPLES = Math.ceil(BUFFER_TARGET_SECONDS * TARGET_SAMPLE_RATE);
const MIN_SAMPLES_FOR_CLASSIFY = Math.floor(TARGET_SAMPLE_RATE * 0.5);
const CLASSIFY_INTERVAL_MS = 600;

let classifier: ((data: Float32Array, options: Record<string, unknown>) => Promise<unknown>) | null = null;
let loading = false;
let lastClassification = 0;
let loadError: Error | null = null;

const audioBuffer = new Float32Array(MAX_BUFFER_SAMPLES);
let bufferWriteIndex = 0;
let bufferFilled = false;

export function isEmotionLoading(): boolean {
  return loading;
}

export function isEmotionReady(): boolean {
  return classifier !== null;
}

export function getEmotionLoadError(): Error | null {
  return loadError;
}

export async function loadEmotionClassifier(
  onProgress?: (progress: number, loaded: number, total: number) => void,
): Promise<void> {
  if (classifier || loading) return;
  loading = true;
  loadError = null;

  try {
    const { pipeline } = await import('@huggingface/transformers');

    classifier = await pipeline(
      'audio-classification',
      'onnx-community/wav2vec2-emotion-recognition-ONNX',
      {
        dtype: 'q4',
        progress_callback: (event: { status?: string; progress?: number; loaded?: number; total?: number }) => {
          if (event.status === 'progress' && event.progress !== undefined && event.loaded !== undefined && event.total !== undefined) {
            onProgress?.(event.progress, event.loaded, event.total);
          }
        },
      } as Record<string, unknown>,
    );
  } catch (err) {
    console.warn('[HumCanvas] Emotion classifier failed to load:', err);
    loadError = err instanceof Error ? err : new Error(String(err));
    classifier = null;
  } finally {
    loading = false;
  }
}

function resampleTo16k(timeDomainData: Float32Array, originalSampleRate: number): Float32Array {
  if (originalSampleRate === TARGET_SAMPLE_RATE) return timeDomainData;
  const ratio = TARGET_SAMPLE_RATE / originalSampleRate;
  const newLength = Math.round(timeDomainData.length * ratio);
  const resampled = new Float32Array(newLength);
  for (let i = 0; i < newLength; i++) {
    const srcIdx = i / ratio;
    const idx = Math.floor(srcIdx);
    const frac = srcIdx - idx;
    resampled[i] = idx + 1 < timeDomainData.length
      ? timeDomainData[idx] * (1 - frac) + timeDomainData[idx + 1] * frac
      : timeDomainData[idx] ?? 0;
  }
  return resampled;
}

export function feedAudioFrame(frame: AudioFrame): void {
  if (!frame?.timeDomainData?.length) return;
  const resampled = resampleTo16k(frame.timeDomainData, frame.sampleRate);
  for (let i = 0; i < resampled.length; i++) {
    audioBuffer[bufferWriteIndex] = resampled[i];
    bufferWriteIndex = (bufferWriteIndex + 1) % MAX_BUFFER_SAMPLES;
    if (bufferWriteIndex === 0) bufferFilled = true;
  }
}

function getBufferedAudio(): Float32Array {
  if (!bufferFilled) {
    return audioBuffer.slice(0, bufferWriteIndex);
  }
  const reordered = new Float32Array(MAX_BUFFER_SAMPLES);
  reordered.set(audioBuffer.slice(bufferWriteIndex));
  reordered.set(audioBuffer.slice(0, bufferWriteIndex), MAX_BUFFER_SAMPLES - bufferWriteIndex);
  return reordered;
}

export async function classifyEmotion(): Promise<EmotionResult | null> {
  if (!classifier) return null;

  const now = performance.now();
  if (now - lastClassification < CLASSIFY_INTERVAL_MS) return null;
  lastClassification = now;

  const buffer = getBufferedAudio();
  if (buffer.length < MIN_SAMPLES_FOR_CLASSIFY) return null;

  try {
    const output = await classifier(buffer, { top_k: 1 });
    const results = output as Array<{ label: string; score: number }>;
    if (results.length === 0) return null;

    const top = results[0];
    if (top.score < 0.15) return null;

    const normalized = LABEL_MAP[top.label.toLowerCase()] ?? 'neutral';

    return {
      label: normalized,
      confidence: top.score,
    };
  } catch (err) {
    console.error('[HumCanvas] Emotion classification error:', err);
    return null;
  }
}

export function classifyEmotionFromProsody(
  frequency: number,
  amplitude: number,
  vibratoDepth: number,
): EmotionResult {
  if (amplitude < 0.03 || frequency <= 0) {
    return { label: 'neutral', confidence: 0.5 };
  }

  if (frequency >= 240 && frequency <= 460 && amplitude > 0.15) {
    return { label: 'happy', confidence: 0.6 };
  }
  if (vibratoDepth > 16) {
    return { label: 'surprise', confidence: 0.6 };
  }
  if (amplitude > 0.35 && frequency >= 180 && frequency <= 350) {
    return { label: 'angry', confidence: 0.55 };
  }
  if (frequency < 200 && amplitude < 0.2) {
    return { label: 'sad', confidence: 0.55 };
  }
  if (frequency > 500) {
    return { label: 'fear', confidence: 0.5 };
  }
  if (frequency >= 200 && frequency <= 320 && amplitude < 0.15) {
    return { label: 'disgust', confidence: 0.45 };
  }

  return { label: 'neutral', confidence: 0.6 };
}

export const EMOTION_COLORS: Record<EmotionLabel, { hue: number; sat: number; light: number }> = {
  happy:     { hue: 45,  sat: 100, light: 62 },
  sad:       { hue: 220, sat: 70,  light: 58 },
  angry:     { hue: 5,   sat: 95,  light: 55 },
  fear:      { hue: 280, sat: 65,  light: 55 },
  neutral:   { hue: 200, sat: 30,  light: 60 },
  surprise:  { hue: 330, sat: 85,  light: 62 },
  disgust:   { hue: 120, sat: 50,  light: 48 },
};
